'use client'

import Link from 'next/link'
import { useLayoutEffect, useState } from 'react'

import { TPopupInfo } from '@/entities/content'
import { UserType } from '@/entities/user'
import { getCookie, getStorage, setCookie, STORAGE_KEY } from '@/shared/lib'
import { Dialog, Image, TextButton } from '@/shared/ui'

const POPUP_COOKIE_NAME = 'main-popup'
const POPUP_COOKIE_VALUE = 'hide'

const getExpireTime = () => {
  return new Date(Date.now() + 1000 * 60 * 60 * 24) // 1 day
}

type Props = {
  popupInfos: TPopupInfo[]
  pageType: UserType
}

export const MainPopup = ({ popupInfos, pageType }: Props) => {
  const [open, setOpen] = useState(false)

  const cookieName = `${pageType}-${POPUP_COOKIE_NAME}`

  const handleOpenChange = (open?: boolean) => {
    if (!open) {
      return
    }

    setOpen(open)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleCloseToday = () => {
    handleClose()
    setCookie(cookieName, POPUP_COOKIE_VALUE, {
      expires: getExpireTime(),
    })
  }

  useLayoutEffect(() => {
    const isBannerHidden = getCookie(cookieName) === POPUP_COOKIE_VALUE
    const hasToShowBlockedUserDialog =
      getStorage(STORAGE_KEY.SHOW_BLOCKED_USER_DIALOG) === 'true'

    if (!isBannerHidden && !hasToShowBlockedUserDialog) {
      setOpen(true)
    }
  }, [])

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        closeButton={false}
        className="gap-0 overflow-hidden rounded-xl p-0"
      >
        <Content
          pageType={pageType}
          popup={popupInfos[0]}
          onClose={handleClose}
          onCloseToday={handleCloseToday}
        />
      </Dialog.Content>
    </Dialog.Root>
  )
}

type ContentProps = {
  pageType: UserType
  popup: TPopupInfo
  onClose: () => void
  onCloseToday: () => void
}

const Content = ({ pageType, popup, onClose, onCloseToday }: ContentProps) => {
  const popupTitle = `${pageType === UserType.HOST ? '호스트' : '게스트'} 메인 팝업`

  return (
    <>
      <Dialog.Header>
        <Dialog.Title className="sr-only">{popupTitle}</Dialog.Title>
        <Dialog.Description className="sr-only">
          {popupTitle}
        </Dialog.Description>
      </Dialog.Header>
      <Link href={popup.link} target="_blank" rel="noopener noreferrer">
        <Image
          src={popup.image}
          alt={popupTitle}
          width={400}
          height={534}
          placeholder
          className="cursor-pointer"
          unoptimized
        />
      </Link>
      <Dialog.Footer className="flex-row justify-between bg-white p-4">
        <TextButton color="gray" onClick={onCloseToday}>
          오늘 하루 보지 않기
        </TextButton>
        <TextButton autoFocus onClick={onClose} color="gray">
          닫기
        </TextButton>
      </Dialog.Footer>
    </>
  )
}
